import type { CopilotProposalSummary } from "@repo/app-types/entities/copilotProposal";
import type { SignalProposalCardData } from "@repo/app-types/entities/signalProposal";
import type { CopilotProposalCardContent } from "../../researchAgent/copilotProposalCard.js";
import {
  buildSignalEditorPathname,
  buildSignalProposalEditLocation,
} from "./signalEditorUrlState.js";

/** What a signal proposal's card in the chat shows and opens. */
export function readSignalProposalCardContent(
  proposal: CopilotProposalSummary<"signal">,
): CopilotProposalCardContent {
  return {
    summary: describeSignalProposal(proposal.data),
    highlights: buildSignalProposalHighlights(proposal),
    editLocation: buildSignalProposalEditLocation({
      signalId:
        proposal.kind === "create_signal" ? undefined : proposal.resource.id,
      proposalId: proposal.id,
    }),
    resourcePathname: buildSignalEditorPathname(proposal.resource.id),
  };
}

/** One sentence on what the signal would detect or what changes. */
function describeSignalProposal(data: SignalProposalCardData): string {
  switch (data.kind) {
    case "create_signal":
      return data.description;
    case "update_signal":
      return data.change_summary;
  }
}

/**
 * The card's highlights: a new signal's field count, or the parts an update
 * changes, with its metadata counted by the fields added, removed, or edited.
 */
function buildSignalProposalHighlights(
  proposal: CopilotProposalSummary<"signal">,
): readonly string[] {
  const { data } = proposal;
  switch (data.kind) {
    case "create_signal":
      return data.metadata_schema.fields.length > 0
        ? [describeFieldCount(data.metadata_schema.fields.length)]
        : [];
    case "update_signal":
      return [
        data.name !== undefined ? "Name" : undefined,
        data.description !== undefined ? "Description" : undefined,
        data.metadata_schema
          ? proposal.changed_metadata_field_count !== undefined
            ? describeFieldCount(proposal.changed_metadata_field_count)
            : "Metadata"
          : undefined,
      ].filter((part) => part !== undefined);
  }
}

function describeFieldCount(count: number): string {
  return `${count} metadata field${count === 1 ? "" : "s"}`;
}
